import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Text } from '@abyss/web/ui/Text';
import { t } from 'i18next';
import React from 'react';

import {
  CareCategories,
  Constants,
  DDP_Code,
  ReverseCoverageTypesCodes,
  TELEHEALTH,
  TypeaheadCoverageTypes,
  VIRTUAL,
  ddpPseudoCode,
} from '../../../../common/Constants';
import { RenderMedicalCoverageLabels } from './DisplayCoverageTypes.utils';
import {
  ContainerInactiveCoverage,
  CoverageIcon,
  CoverageType,
  InactiveCoverage,
} from './KeywordSearchDropdown.styled';

const notInYourBenefits = `\u00A0(${Constants.NOT_IN_YOUR_BENEFITS})`;
const notInYourExperience = `\u00A0(${Constants.NOT_IN_YOUR_EXPERIENCE})`;
const dataTestIdForInEligibileCoverageType = 'not-a-eligibile-coverage-type';

export const DonotDisturbIcon = () => (
  <IconSymbol
    color="$gray7"
    css={{
      '&.abyss-icon-symbol': { marginBottom: '-4px', marginRight: '2px' },
    }}
    data-testid="disabled-coverage-type-icon"
    icon="do_not_disturb"
    size={16}
    variant="outlined"
  />
);
type Props = {
  item: any;
  getCoverageType: Array<string>;
  hideDisabledTypeahead: boolean;
  memberDDP: string;
  isVirtualCareMedicalSuppressed?: boolean;
  isVirtualCareRollUpCodeSuppressed?: boolean;
  isTypeaheadBehavioralHealthTagSuppressed: boolean;
  useCategoryForCoverageType?: boolean;
  isEapRollUpCodeSupressed?: boolean;
  enableAutocompleteTaggingChanges?: boolean;
};

const CategoryCoverageTypeMap: Record<string, string> = {
  'Primary Care': 'M',
  'Medical Specialists': 'M',
  'Behavioral Health': 'B',
  Facilities: 'M',
  'Health Supplies': 'M',
  Vision: 'V',
  Dental: 'D',
};

export const getCoverageTypeFromCategory = (category: string): string[] => {
  if (!category || category === CareCategories.ALL) return [];
  return category
    .split(',')
    .map((eachCat) => CategoryCoverageTypeMap[eachCat.trim()])
    .filter(Boolean);
};

export const DisplayCoverageTypes = ({
  item,
  getCoverageType,
  hideDisabledTypeahead,
  memberDDP,
  isVirtualCareMedicalSuppressed = false,
  isVirtualCareRollUpCodeSuppressed = false,
  isTypeaheadBehavioralHealthTagSuppressed = false,
  isEapRollUpCodeSupressed = false,
  useCategoryForCoverageType = false,
  enableAutocompleteTaggingChanges = false,
}: Props) => {
  const coverageType = useCategoryForCoverageType
    ? getCoverageTypeFromCategory(item?.category)
    : item?.coverageType;

  const isVirtualSuggestion =
    item?.suggestion?.toLowerCase().includes(VIRTUAL) ||
    item?.suggestion?.toLowerCase().includes(TELEHEALTH);
  const notInBenefitOrExpInfoText = enableAutocompleteTaggingChanges
    ? notInYourExperience
    : notInYourBenefits;

  const disableDDP =
    item?.pseudoVerticals == ddpPseudoCode &&
    !memberDDP?.includes(DDP_Code[item?.suggestion?.toLowerCase()]) &&
    memberDDP !== DDP_Code['All'];

  const isDisplayingMedicalNotInYourBenefits =
    isVirtualCareMedicalSuppressed &&
    isVirtualSuggestion &&
    coverageType?.length >= 2;

  const disabledMedicalCoverageLabel =
    isVirtualCareRollUpCodeSuppressed && coverageType?.length >= 2;

  const hideGreenTags = enableAutocompleteTaggingChanges;
  const isUnderSelectedCoverageType =
    coverageType?.some((value: string) => getCoverageType?.includes(value)) ||
    false;

  return (
    <React.Fragment>
      {coverageType?.some((value: string) =>
        getCoverageType?.includes(value)
      ) && (
        <React.Fragment>
          {RenderMedicalCoverageLabels(
            isVirtualCareMedicalSuppressed,
            getCoverageType,
            coverageType,
            disableDDP,
            isDisplayingMedicalNotInYourBenefits,
            disabledMedicalCoverageLabel,
            dataTestIdForInEligibileCoverageType,
            notInBenefitOrExpInfoText,
            hideGreenTags
          )}
          {!hideGreenTags &&
            coverageType?.includes(
              ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']
            ) &&
            getCoverageType?.includes(
              ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']
            ) &&
            !isVirtualCareRollUpCodeSuppressed &&
            !isTypeaheadBehavioralHealthTagSuppressed &&
            !isEapRollUpCodeSupressed && (
              <Text css={CoverageType}>
                <div
                  style={{
                    backgroundColor: '#F2F8E6',
                  }}
                >
                  <IconSymbol
                    color="$success1"
                    css={CoverageIcon}
                    icon="psychology"
                    size={16}
                    variant="outlined"
                  />
                  {t(TypeaheadCoverageTypes.BEHAVIORAL)}
                </div>
              </Text>
            )}

          {coverageType?.includes(
            ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']
          ) &&
            getCoverageType?.includes(
              ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']
            ) &&
            (isVirtualCareRollUpCodeSuppressed || isEapRollUpCodeSupressed) && (
              <Text
                css={InactiveCoverage}
                data-testid={dataTestIdForInEligibileCoverageType}
              >
                <div style={{ display: 'flex' }}>
                  {!isTypeaheadBehavioralHealthTagSuppressed ? (
                    <ContainerInactiveCoverage>
                      <DonotDisturbIcon />
                      {t(TypeaheadCoverageTypes.BEHAVIORAL)}
                    </ContainerInactiveCoverage>
                  ) : null}
                  {notInBenefitOrExpInfoText}
                </div>
              </Text>
            )}

          {!hideGreenTags &&
            coverageType?.includes(ReverseCoverageTypesCodes.VISION) &&
            getCoverageType?.includes(ReverseCoverageTypesCodes.VISION) && (
              <Text css={CoverageType}>
                <div style={{ backgroundColor: '#F2F8E6' }}>
                  <IconSymbol
                    color="$success1"
                    css={CoverageIcon}
                    icon="remove_red_eye"
                    size={16}
                    variant="outlined"
                  />
                  {t(TypeaheadCoverageTypes.VISION)}
                </div>
              </Text>
            )}

          {!hideGreenTags &&
            coverageType?.includes(ReverseCoverageTypesCodes.DENTAL) &&
            getCoverageType?.includes(ReverseCoverageTypesCodes.DENTAL) && (
              <Text css={CoverageType}>
                <div style={{ backgroundColor: '#F2F8E6' }}>
                  <IconSymbol
                    color="$success1"
                    css={{
                      marginRight: '2px',
                      position: 'relative',
                      top: '4px',
                    }}
                    icon="Dentistry"
                    size="16px"
                    variant="outlined"
                  />
                  {t(TypeaheadCoverageTypes.DENTAL)}
                </div>
              </Text>
            )}
        </React.Fragment>
      )}

      {!hideDisabledTypeahead &&
        !coverageType?.every((value: string) =>
          getCoverageType?.includes(value)
        ) && (
          <React.Fragment>
            {!isUnderSelectedCoverageType &&
              coverageType?.includes(ReverseCoverageTypesCodes.MEDICAL) && (
                <Text
                  css={InactiveCoverage}
                  data-testid={dataTestIdForInEligibileCoverageType}
                >
                  <div style={{ display: 'flex' }}>
                    <ContainerInactiveCoverage>
                      <DonotDisturbIcon />
                      {t(TypeaheadCoverageTypes.MEDICAL)}
                    </ContainerInactiveCoverage>
                    {notInBenefitOrExpInfoText}
                  </div>
                </Text>
              )}

            {!isUnderSelectedCoverageType &&
              coverageType?.includes(
                ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']
              ) && (
                <Text
                  css={InactiveCoverage}
                  data-testid={dataTestIdForInEligibileCoverageType}
                >
                  <div style={{ display: 'flex' }}>
                    <ContainerInactiveCoverage>
                      <DonotDisturbIcon />
                      {t(TypeaheadCoverageTypes.BEHAVIORAL)}
                    </ContainerInactiveCoverage>
                    {notInBenefitOrExpInfoText}
                  </div>
                </Text>
              )}

            {!isUnderSelectedCoverageType &&
              coverageType?.includes(ReverseCoverageTypesCodes.VISION) && (
                <Text
                  css={InactiveCoverage}
                  data-testid={dataTestIdForInEligibileCoverageType}
                >
                  <div style={{ display: 'flex' }}>
                    <ContainerInactiveCoverage>
                      <DonotDisturbIcon />
                      {t(TypeaheadCoverageTypes.VISION)}
                    </ContainerInactiveCoverage>
                    {notInBenefitOrExpInfoText}
                  </div>
                </Text>
              )}

            {!isUnderSelectedCoverageType &&
              coverageType?.includes(ReverseCoverageTypesCodes.DENTAL) && (
                <Text
                  css={InactiveCoverage}
                  data-testid={dataTestIdForInEligibileCoverageType}
                >
                  <div style={{ display: 'flex' }}>
                    <ContainerInactiveCoverage>
                      <DonotDisturbIcon />
                      {t(TypeaheadCoverageTypes.DENTAL)}
                    </ContainerInactiveCoverage>
                    {notInBenefitOrExpInfoText}
                  </div>
                </Text>
              )}
          </React.Fragment>
        )}
    </React.Fragment>
  );
};
